import React from "react";

export default function KbToolbar({ editor, disabled = false, onUploadImage }) {
  if (!editor) return null;
  const button = (label, action, active = false, title = label, enabled = true) => (
    <button key={label} type="button" title={title} disabled={disabled || !enabled}
      onMouseDown={(event) => event.preventDefault()} onClick={action}
      className={`kbv2-tool ${active ? "kbv2-tool-active" : ""}`}>{label}</button>
  );
  const addLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave blank to remove)", previous);
    if (url === null) return;
    if (!url.trim()) return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    try {
      const parsed = new URL(url.trim(), window.location.origin);
      if (!["https:", "http:", "mailto:"].includes(parsed.protocol)) return window.alert("Only HTTP, HTTPS and email links are supported.");
      editor.chain().focus().extendMarkRange("link").setLink({ href: parsed.href }).run();
    } catch { window.alert("Please enter a valid link."); }
  };
  const addCallout = (kind) => {
    const label =
      kind === "warning"
        ? "Important: "
        : kind === "success"
        ? "Tip: "
        : "Note: ";

    editor
      .chain()
      .focus()
      .insertContent([
        {
          type: "kbCallout",
          attrs: { kind },
          content: [
            {
              type: "paragraph",
              content: [{ type: "text", text: label }],
            },
          ],
        },
        {
          type: "paragraph",
        },
      ])
      .run();
  };
  const inTable = editor.isActive("table");
  const inImage = editor.isActive("image");
  return (
    <div className="kbv2-toolbar" role="toolbar" aria-label="Article formatting">
      {button("Undo", () => editor.chain().focus().undo().run())}
      {button("Redo", () => editor.chain().focus().redo().run())}
      <span className="kbv2-separator" />
      {button("P", () => editor.chain().focus().setParagraph().run(), editor.isActive("paragraph"), "Paragraph")}
      {[1, 2, 3].map((level) => button(`H${level}`, () => editor.chain().focus().toggleHeading({ level }).run(), editor.isActive("heading", { level })))}
      <span className="kbv2-separator" />
      {button("B", () => editor.chain().focus().toggleBold().run(), editor.isActive("bold"), "Bold")}
      {button("I", () => editor.chain().focus().toggleItalic().run(), editor.isActive("italic"), "Italic")}
      {button("S", () => editor.chain().focus().toggleStrike().run(), editor.isActive("strike"), "Strikethrough")}
      {button("Code", () => editor.chain().focus().toggleCode().run(), editor.isActive("code"))}
      <span className="kbv2-separator" />
      {button("• List", () => editor.chain().focus().toggleBulletList().run(), editor.isActive("bulletList"))}
      {button("1. List", () => editor.chain().focus().toggleOrderedList().run(), editor.isActive("orderedList"))}
      {button("Quote", () => editor.chain().focus().toggleBlockquote().run(), editor.isActive("blockquote"))}
      {button("Code block", () => editor.chain().focus().toggleCodeBlock().run(), editor.isActive("codeBlock"))}
      {button("Rule", () => editor.chain().focus().setHorizontalRule().run())}
      {button("Link", addLink, editor.isActive("link"))}
      <span className="kbv2-separator" />
      {button("Table +", () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())}
      {button("Row +", () => editor.chain().focus().addRowAfter().run(), false, "Add row", inTable)}
      {button("Row −", () => editor.chain().focus().deleteRow().run(), false, "Delete row", inTable)}
      {button("Col +", () => editor.chain().focus().addColumnAfter().run(), false, "Add column", inTable)}
      {button("Col −", () => editor.chain().focus().deleteColumn().run(), false, "Delete column", inTable)}
      {button("Table ×", () => editor.chain().focus().deleteTable().run(), false, "Delete table", inTable)}
      <span className="kbv2-separator" />
      {button("Image", onUploadImage, false, "Upload image or paste a screenshot")}
      {button("50%", () => editor.chain().focus().updateAttributes("image", { width: "50%" }).run(), false, "Set image width", inImage)}
      {button("100%", () => editor.chain().focus().updateAttributes("image", { width: "100%" }).run(), false, "Set image width", inImage)}
      <span className="kbv2-separator" />
      {button("Info", () => addCallout("info"))}
      {button("Warning", () => addCallout("warning"))}
      {button("Success", () => addCallout("success"))}
    </div>
  );
}
